import { emptyGame } from './game.js';
import { computeCharge } from './charge.js';
import { chapterLengthMs, openingPhase } from './schedule.js';
import { PROFESSION_TAG, taskKey, tasksForChapter } from './tasks.js';
import type { Chapter, GameState, Profession, Seat, ShowState, TaskState } from './types.js';
import type { GameSet, Slot } from './thirty.js';

export const MAX_SEATS = 12;
export const MIN_CREW = 4;
/**
 * Twelve at most, the two at the round end being the backup (Marek, 2026-09-29): an evening sells
 * ten seats, the ten places along the sides with their plates and coasters.
 */
export const MAX_CREW = 12;

/** How many crew may hold one post — two once there are more than six. */
export function professionCapacity(crewSize: number): number {
  return Math.max(1, Math.ceil(crewSize / 6));
}

export function emptySeat(seat: number): Seat {
  return {
    seat,
    token: null,
    callsign: null,
    profession: null,
    alignment: null,
    volunteer: null,
    brig: false,
    connected: false,
    lastSeenAt: 0,
    pass: 0,
    heldAt: null,
  };
}

/**
 * How long the door may hold a seat for a guest who has not scanned in. Long enough for a guest
 * who is handed their code and hangs up their coat before scanning it; short enough that a phone
 * console closed mid-check-in does not keep a seat from the rest of the evening.
 */
export const SEAT_HOLD_MS = 30 * 60_000;

/** Whether the door is holding this seat right now. A hold past its time counts as none. */
export function seatHeld(seat: Pick<Seat, 'heldAt'>, now: number): boolean {
  return seat.heldAt !== null && seat.heldAt !== undefined && now - seat.heldAt < SEAT_HOLD_MS;
}

export interface CreateShowStateOptions {
  voyageId: string;
  date: string;
  now: number;
  crewSize?: number;
  seeds?: ShowState['seeds'];
  gameSet?: GameSet;
}

export function createShowState(opts: CreateShowStateOptions): ShowState {
  const crewSize = opts.crewSize ?? MAX_CREW;
  const state: ShowState = {
    voyageId: opts.voyageId,
    date: opts.date,
    chapter: 0,
    phase: openingPhase(0),
    chapterStartedAt: opts.now,
    phaseStartedAt: opts.now,
    chapterLengthMs: chapterLengthMs(0),
    hold: { active: false, since: null, accumulatedMs: 0 },
    crewSize,
    // Always the thirty (Marek, 2026-09-30: "it should be the new improved games always, automatically").
    gameSet: opts.gameSet ?? 'thirty',
    seats: Array.from({ length: crewSize }, (_, i) => emptySeat(i + 1)),
    tasks: {},
    votes: {},
    playerContribution: 0,
    chargeOverride: null,
    charge: 0,
    seeds: opts.seeds ? [...opts.seeds] : [],
    cues: {},
    integrations: { hue: 'off', spotify: 'off', cloud: 'connecting' },
    taps: {},
    scores: {},
    stats: {},
    tableGame: null,
    signals: {},
    wrappedAt: null,
    // Doors open by default; the operator closes them, and seals at docking.
    access: { open: true, opensAt: null, closesAt: null, sealed: false, sealedAt: null },
    watching: {},
    game: emptyGame(),
    log: [],
  };
  return withCharge(state, opts.now);
}

/**
 * Bring a stored ShowState up to date.
 *
 * Rooms persist across deploys, so a room written before a field existed comes back
 * without it — and reading `state.access.sealed` on that throws, which would turn
 * every guest away with a 500. Anything missing is filled with the same defaults a
 * fresh room gets.
 */
export function migrateState(stored: ShowState): ShowState {
  const fresh = createShowState({
    voyageId: stored.voyageId,
    date: stored.date,
    now: Date.now(),
    crewSize: stored.crewSize,
  });
  return {
    ...fresh,
    ...stored,
    // A room written before this field existed would count down from 1970.
    phaseStartedAt: stored.phaseStartedAt ?? stored.chapterStartedAt ?? fresh.phaseStartedAt,
    // Always the thirty: a room saved on the classic six before departure comes back on the thirty; one
    // mid-evening keeps its own, since its games are numbered within it.
    gameSet: stored.chapter >= 1 ? (stored.gameSet ?? 'classic') : 'thirty',
    access: { ...fresh.access, ...(stored.access ?? {}) },
    watching: stored.watching ?? {},
    hold: { ...fresh.hold, ...(stored.hold ?? {}) },
    integrations: { ...fresh.integrations, ...(stored.integrations ?? {}) },
    cues: stored.cues ?? {},
    taps: stored.taps ?? {},
    // Rooms written before the games were scored: nobody has points yet, and nothing is counted.
    scores: stored.scores ?? {},
    stats: stored.stats ?? {},
    tableGame: stored.tableGame ?? null,
    tasks: stored.tasks ?? {},
    votes: stored.votes ?? {},
    seeds: stored.seeds ?? [],
    log: stored.log ?? [],
    // Rooms written before the saboteur game: every seat and the game get their defaults.
    seats: (stored.seats ?? fresh.seats).map((s) => ({
      ...s,
      volunteer: s.volunteer ?? null,
      brig: s.brig ?? false,
      pass: s.pass ?? 0,
      // Rooms written before the door held seats: nothing is held.
      heldAt: s.heldAt ?? null,
    })),
    game: migrateGame({ ...fresh.game, ...(stored.game ?? {}) }),
  };
}

/** Alarms written before FIX was handed to named seats: nobody called yet, so the next tick calls them. */
function migrateGame(game: GameState): GameState {
  return {
    ...game,
    anomalies: game.anomalies.map((a) => ({ ...a, called: a.called ?? [], calledAt: a.calledAt ?? {}, seen: a.seen ?? {} })),
  };
}

/** Recompute the derived charge. Called at the end of every reduce. */
export function withCharge(state: ShowState, now: number): ShowState {
  const charge = computeCharge(state, now);
  return charge === state.charge ? state : { ...state, charge };
}

/**
 * Whether a pass works right now. Sealed beats everything; then the operator's door;
 * then the time window.
 */
export function accessOpen(state: ShowState, now: number): { ok: boolean; reason: string } {
  if (state.access.sealed) return { ok: false, reason: 'this voyage has docked' };
  if (!state.access.open) return { ok: false, reason: 'boarding is not open yet' };
  if (state.access.opensAt !== null && now < state.access.opensAt) {
    return { ok: false, reason: 'boarding has not opened yet' };
  }
  if (state.access.closesAt !== null && now > state.access.closesAt) {
    return { ok: false, reason: 'boarding has closed' };
  }
  return { ok: true, reason: '' };
}

/** How many crew are looking over this seat's shoulder right now. */
export function watcherCount(state: ShowState, seat: number): number {
  return Object.values(state.watching).filter((target) => target === seat).length;
}

/** Who is watching this seat. */
export function watchersOf(state: ShowState, seat: number): number[] {
  return Object.entries(state.watching)
    .filter(([, target]) => target === seat)
    .map(([watcher]) => Number(watcher))
    .sort((a, b) => a - b);
}

/**
 * Who is still playing, for the "who shall I watch?" list. A seat counts as playing
 * while a game dealt to it this chapter is unfinished (`dealGames`).
 */
export function stillPlaying(state: ShowState): number[] {
  if (state.chapter === 0) return [];
  const { players } = dealGames(state, state.chapter);
  const out = new Set<number>();
  players.forEach((p, i) => {
    for (const seat of p) if (!state.tasks[taskKey(state.chapter, i, seat)]?.done) out.add(seat);
  });
  return [...out].sort((a, b) => a - b);
}

/** Seats a latecomer can still take. */
export function openSeats(state: ShowState): number[] {
  return state.seats.filter((s) => s.token === null).map((s) => s.seat);
}

export function seatOf(state: ShowState, seat: number): Seat | undefined {
  return state.seats.find((s) => s.seat === seat);
}

export function connectedSeats(state: ShowState): Seat[] {
  return state.seats.filter((s) => s.connected);
}

/** How many crew already hold this post, ignoring one seat (the one being changed). */
export function professionCount(state: ShowState, profession: Profession, exceptSeat = -1): number {
  return state.seats.filter((s) => s.profession === profession && s.seat !== exceptSeat).length;
}

export function tallyVote(state: ShowState, voteId: string): Record<string, number> {
  const ballots = state.votes[voteId] ?? {};
  const out: Record<string, number> = {};
  for (const option of Object.values(ballots)) out[option] = (out[option] ?? 0) + 1;
  return out;
}

/**
 * The game of this seat's own post in a chapter, or `undefined` if they have no post yet. Who
 * actually plays what — stand-ins included — is `gamesOf`; this is only the post's own game.
 */
export function ownTaskIndex(state: ShowState, chapter: Chapter, seat: number): number | undefined {
  const index = ownGameIndex(state, chapter, seat);
  return index === -1 ? undefined : index;
}

/**
 * In the thirty, which of its post's two games a seat plays all evening: the post's first holder
 * by seat number plays the first holder's, anyone else holding it a second holder's. Every holder
 * checked in counts, a guest voted out too, so the order never shifts under someone mid-evening.
 */
export function slotOf(state: Pick<DealState, 'seats'>, seat: number): Slot {
  const s = state.seats.find((x) => x.seat === seat);
  if (!s?.profession) return 'A';
  const holders = state.seats
    .filter((x) => x.profession === s.profession && x.callsign !== null)
    .map((x) => x.seat)
    .sort((a, b) => a - b);
  return holders.indexOf(seat) <= 0 ? 'A' : 'B';
}

/** The index of a seat's own game this era, or −1 if it has no post yet. */
export function ownGameIndex(state: DealState, chapter: Chapter, seat: number): number {
  const s = state.seats.find((x) => x.seat === seat);
  if (!s?.profession) return -1;
  const tag = PROFESSION_TAG[s.profession];
  const slot = state.gameSet === 'thirty' ? slotOf(state, seat) : undefined;
  return tasksForChapter(chapter, state.gameSet).findIndex(
    (t) => t.tag === tag && (slot === undefined || t.slot === slot),
  );
}

// ---------------------------------------------------------------------------
// Who plays which game (Marek's test evening, 2026-09-28: a post nobody held left its game on
// every phone as "NOBODY" at 0 % for the whole era, and the room would take it from nobody).

/** What the deal reads of a seat. The room, the phones, the TV and the watch cues all have these. */
export type DealSeat = Pick<Seat, 'seat' | 'callsign' | 'profession' | 'brig'>;
/** What the deal reads of a game in play. */
export type DealTask = Pick<TaskState, 'chapter' | 'index' | 'seat' | 'done'>;
/** Enough of a room to deal from: a ShowState, a phone's or a console's copy of it, or the cues'. */
export interface DealState {
  seats: readonly DealSeat[];
  tasks: Readonly<Record<string, DealTask>>;
  /** Which games the evening deals; the classic six when absent. */
  gameSet?: GameSet;
}

/**
 * A guest who is playing: checked in, with a call sign and a post, and not voted out. The one
 * definition for the games dealt each era and for the table games.
 */
export function isAboard(seat: Pick<Seat, 'callsign' | 'profession' | 'brig'>): boolean {
  return seat.callsign !== null && seat.profession !== null && !seat.brig;
}

export interface GameDeal {
  /** By game (task index): the guests aboard who play it this era, in seat order. */
  players: number[][];
  /** By game: the guest standing in for a post nobody aboard holds, or null. */
  standIns: (number | null)[];
}

/**
 * Who plays which game this era. Everyone aboard plays their own post's game. A game whose post
 * nobody aboard holds — a small table, a guest who left, the only holder voted out — goes to a
 * stand-in, who plays it as well as their own: the guest with the fewest games, a holder of a
 * doubled post first (their own game has a second player), and then round the table by era and
 * game, so the same guest is not always the one asked.
 *
 * Worked out from the room as it is now, so a guest added or removed mid-evening changes the deal
 * at once. Two things keep it steady while it changes: a game a guest has already begun this era
 * stays theirs, and a game already finished this era (by a guest since voted out, say) needs
 * nobody else.
 */
export function dealGames(state: DealState, chapter: Chapter): GameDeal {
  const games = tasksForChapter(chapter, state.gameSet);
  const players: number[][] = games.map(() => []);
  const standIns: (number | null)[] = games.map(() => null);
  const aboard = state.seats.filter(isAboard).map((s) => s.seat).sort((a, b) => a - b);
  if (games.length === 0 || aboard.length === 0) return { players, standIns };
  const on = new Set(aboard);
  const postGame = (seat: DealSeat) => ownGameIndex(state, chapter, seat.seat);

  for (const s of state.seats) {
    if (!on.has(s.seat)) continue;
    const i = postGame(s);
    if (i >= 0) players[i]!.push(s.seat);
  }
  const holders = players.map((p) => p.length);
  const finished = new Set<number>();
  for (const t of Object.values(state.tasks)) {
    if (t.chapter !== chapter || !players[t.index]) continue;
    if (t.done) finished.add(t.index);
    // Begun is kept: a stand-in's game does not vanish from under them when its post fills again.
    if (on.has(t.seat) && !players[t.index]!.includes(t.seat)) players[t.index]!.push(t.seat);
  }

  const count = (seat: number) => players.reduce((n, p) => n + (p.includes(seat) ? 1 : 0), 0);
  const doubled = (seat: number) => {
    const s = state.seats.find((x) => x.seat === seat);
    const i = s ? postGame(s) : -1;
    return i >= 0 && (holders[i] ?? 0) >= 2;
  };
  games.forEach((game, i) => {
    if (players[i]!.length > 0 || finished.has(i)) return;
    // In the thirty a second holder's game is there for a second holder, and nobody stands in for it.
    if (game.slot === 'B') return;
    const turn = (chapter * 2 + i) % aboard.length;
    const rank = (seat: number) => (aboard.indexOf(seat) - turn + aboard.length) % aboard.length;
    const standIn = [...aboard].sort(
      (a, b) => count(a) - count(b) || Number(doubled(b)) - Number(doubled(a)) || rank(a) - rank(b),
    )[0]!;
    players[i]!.push(standIn);
    standIns[i] = standIn;
  });
  for (const p of players) p.sort((a, b) => a - b);
  return { players, standIns };
}

/**
 * The games this seat may play this era, its own post's first: its post's game, whatever it was
 * dealt to stand in for, and anything it has already begun. A guest voted out keeps their own
 * game (they "can still play"), though the era no longer waits for them.
 */
export function gamesOf(state: DealState, chapter: Chapter, seat: number): number[] {
  const deal = dealGames(state, chapter);
  const own = ownGameIndex(state, chapter, seat);
  const out = new Set<number>();
  if (own >= 0) out.add(own);
  deal.players.forEach((p, i) => {
    if (p.includes(seat)) out.add(i);
  });
  for (const t of Object.values(state.tasks)) if (t.chapter === chapter && t.seat === seat) out.add(t.index);
  return [...out].sort((a, b) => (a === own ? -1 : b === own ? 1 : a - b));
}

/** Who plays one game this era: the guests aboard it was dealt to. */
export function playersOf(state: DealState, chapter: Chapter, index: number): number[] {
  return dealGames(state, chapter).players[index] ?? [];
}

/** The deal as a list, one line per guest and game dealt to them: what a stop's games add up to. */
export function dealtGames(state: DealState, chapter: Chapter): { seat: number; index: number }[] {
  return dealGames(state, chapter).players.flatMap((p, index) => p.map((seat) => ({ seat, index })));
}

/** Every guest aboard has finished every game dealt to them this era. */
export function allDealtDone(state: DealState, chapter: Chapter): boolean {
  const { players } = dealGames(state, chapter);
  if (!players.some((p) => p.length > 0)) return false;
  return players.every((p, i) => p.every((seat) => state.tasks[taskKey(chapter, i, seat)]?.done === true));
}

export function taskStateOf(
  state: ShowState,
  chapter: Chapter,
  index: number,
  seat: number,
): TaskState | undefined {
  return state.tasks[taskKey(chapter, index, seat)];
}

/** Open RELAY missions, for the steward console. */
export function openRelays(state: ShowState): TaskState[] {
  return Object.values(state.tasks).filter((t) => t.relay !== undefined && !t.done);
}
